import { rem } from 'polished'
import React, { useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import CuratedTripsIntro from './CuratedTripsIntro/CuratedTripsIntro'
import CuratedTripsListGrid from './CuratedTripsListGrid'
import CustomerSupportBlock from './CustomerSupportBlock'

import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import HeadData from 'components/Common/HeadData/HeadData'
import Hr from 'components/Common/Hr'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { scrollToId } from 'lib/window/scrollUtils'
import HeaderLogo from 'tripPlanner/components/HeaderLogo'
import TripModal from 'tripPlanner/components/TripModal'
import { usePaginatedCuratedTrips } from 'tripPlanner/hooks/api/curatedTrip'
import { useTrips } from 'tripPlanner/hooks/api/trip'
import LoadingPage from 'tripPlanner/pages/Loading'

const HERO_HEIGHT = 320

const VIDEO_URL =
  'https://player.vimeo.com/progressive_redirect/playback/797541356/rendition/720p/file.mp4?loc=external&signature=d359fad9cb33428ebefd7cb73afb233b919028b2f57b2edf8233f1632865f760'

const Page = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding-bottom: ${rem(120)};
`

const HeroSection = styled.div`
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  width: 100%;
  height: ${rem(HERO_HEIGHT)};
  display: flex;
  justify-content: center;
  align-items: center;

  & > video {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 1;
  }
`

const HeroTitle = styled(LayoutContainer)`
  position: relative;
  z-index: 2;
`

const HeroSectionLogo = styled(HeaderLogo)`
  width: 157px;

  ${mediaQueryUp.tablet} {
    width: 195px;
  }
`

const CURATED_TRIPS_GRID_ID = 'curated-trips-grid'

function CuratedTripsPage() {
  const { ref, inView } = useInView()

  const { data, fetchNextPage, status, isFetchingNextPage, hasMore } =
    usePaginatedCuratedTrips()
  const curatedTrips = data // Query has placeholder data

  const { data: trips } = useTrips()
  const hasTrips = trips && trips.length > 0

  const scrollToCuratedTrips = useCallback(() => {
    scrollToId(CURATED_TRIPS_GRID_ID)
  }, [])

  const fetch = useCallback(() => {
    if (fetchNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage])

  useEffect(() => {
    if (inView) {
      fetch()
    }
  }, [inView, fetch])

  return (
    <Page>
      <HeadData
        title="Curated Trips - Trip Planner"
        description="Perfectly curated trips that do all the planning for you"
      />
      <TripModal />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>
      <VerticalSpacer gap={40}>
        <HeroSection>
          <video controls={false} autoPlay playsInline loop muted>
            <source src={VIDEO_URL} type="video/mp4" width="100%" height="100%" />
          </video>
          <HeroTitle>
            <VerticalSpacer gap={20}>
              <HeroSectionLogo />
              <Heading
                colour="neutral-eight"
                variant="heading2"
                style={{ gridArea: 'title' }}
              >
                Take the hassle out of planning your next adventure
              </Heading>
              <Group direction="horizontal" gap={16}>
                <TextButton
                  variant="ghost"
                  kind="primary"
                  size="medium"
                  shape="square"
                  to="/trip-planner/about"
                >
                  Learn more
                </TextButton>
                {hasTrips && (
                  <TextButton
                    variant="default"
                    kind="primary"
                    size="medium"
                    shape="square"
                    to="/trip-planner"
                  >
                    View trips
                  </TextButton>
                )}
              </Group>
            </VerticalSpacer>
          </HeroTitle>
        </HeroSection>
        <div>
          {status == 'loading' && <LoadingPage />}
          <LayoutContainer>
            <CuratedTripsIntro onClickBrowse={scrollToCuratedTrips} />
            {status == 'success' && (
              <CuratedTripsListGrid
                id={CURATED_TRIPS_GRID_ID}
                curatedTrips={curatedTrips}
              >
                <Group direction="horizontal" horizontalAlign="center" ref={ref}>
                  {hasMore && (
                    <TextButton
                      kind="primary"
                      size="large"
                      shape="square"
                      onClick={fetch}
                      disabled={isFetchingNextPage}
                    >
                      Load More trips
                    </TextButton>
                  )}
                </Group>
              </CuratedTripsListGrid>
            )}

            <VerticalSpacer gap={32}>
              <Hr size="md" />
              <CustomerSupportBlock />
            </VerticalSpacer>
          </LayoutContainer>
        </div>
      </VerticalSpacer>
    </Page>
  )
}

export default CuratedTripsPage
